import { useNotificationPreferences } from '../hooks/useNotificationPreferences';

/**
 * Notification Preferences Component
 * Allows users to manage email notification settings
 */
export function NotificationPreferences({ onClose }) {
  const {
    preferences,
    loading,
    saving,
    error,
    togglePreference,
    enableAll,
    disableAll
  } = useNotificationPreferences();

  if (!window.t24Governance.isLoggedIn) {
    return (
      <div className="t24-notification-prefs">
        <div className="prefs-header">
          <h2>Notification Preferences</h2>
          {onClose && (
            <button className="prefs-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          )}
        </div>
        <div className="prefs-login-required">
          <span className="login-icon">🔒</span>
          <p>Please log in to manage your notification preferences.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="t24-notification-prefs">
      <div className="prefs-header">
        <h2>
          <span className="header-icon">📧</span>
          Email Notification Preferences
        </h2>
        {onClose && (
          <button className="prefs-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </div>

      {loading && (
        <div className="prefs-loading">
          <div className="t24-loading-spinner"></div>
          <p>Loading preferences...</p>
        </div>
      )}

      {error && (
        <div className="prefs-error">
          <span className="error-icon">⚠️</span>
          <p>Error: {error}</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="prefs-description">
            <p>Choose which email notifications you want to receive about governance activities.</p>
          </div>

          <div className="prefs-options">
            {/* Proposal Created */}
            <label className="pref-option">
              <input
                type="checkbox"
                checked={preferences.proposal_created}
                onChange={() => togglePreference('proposal_created')}
                disabled={saving}
              />
              <div className="pref-content">
                <div className="pref-title">
                  <span className="pref-icon">📝</span>
                  New Proposals Created
                </div>
                <div className="pref-description">
                  Get notified when new proposals are created in categories you have access to.
                </div>
              </div>
            </label>

            {/* Voting Deadline */}
            <label className="pref-option">
              <input
                type="checkbox"
                checked={preferences.voting_deadline}
                onChange={() => togglePreference('voting_deadline')}
                disabled={saving}
              />
              <div className="pref-content">
                <div className="pref-title">
                  <span className="pref-icon">⏰</span>
                  Voting Deadline Reminders
                </div>
                <div className="pref-description">
                  Receive reminders 24 hours before voting deadlines for active proposals.
                </div>
              </div>
            </label>

            {/* Vote Registered */}
            <label className="pref-option">
              <input
                type="checkbox"
                checked={preferences.vote_registered}
                onChange={() => togglePreference('vote_registered')}
                disabled={saving}
              />
              <div className="pref-content">
                <div className="pref-title">
                  <span className="pref-icon">✅</span>
                  Vote Confirmation
                </div>
                <div className="pref-description">
                  Get confirmation emails when you successfully cast or change your vote.
                </div>
              </div>
            </label>

            {/* Proposal Status Change */}
            <label className="pref-option">
              <input
                type="checkbox"
                checked={preferences.proposal_status_change}
                onChange={() => togglePreference('proposal_status_change')}
                disabled={saving}
              />
              <div className="pref-content">
                <div className="pref-title">
                  <span className="pref-icon">🔄</span>
                  Status Updates
                </div>
                <div className="pref-description">
                  Be notified when proposals you voted on change status (e.g., approved, rejected, completed).
                </div>
              </div>
            </label>
          </div>

          <div className="prefs-actions">
            <button
              className="pref-action-btn secondary"
              onClick={enableAll}
              disabled={saving}
            >
              Enable All
            </button>
            <button
              className="pref-action-btn secondary"
              onClick={disableAll}
              disabled={saving}
            >
              Disable All
            </button>
          </div>

          {saving && (
            <div className="prefs-saving">
              <div className="t24-loading-spinner small"></div>
              <span>Saving preferences...</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
